import { VoicemailFilters } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import { Direction as CallDirection, CallStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';

export enum AllCallsTable {
  RecentCalls = 'recent-calls',
  Voicemails = 'voicemails',
  Unread_Voicemails = 'unread-voicemails',
  Analytics = 'analytics',
}

export type PaginationSettings = {
  pageSize: number;
  pageNumber: number;
};

export type VoicemailFiltersType = {
  locationIds: string[];
  syncDeviceSipProfileId?: string;
  filterOptions: VoicemailFilters;
};

type VoicemailPageSetting = PaginationSettings & {
  filters: VoicemailFiltersType | undefined;
};

export type CallRecordsFiltersType = {
  locationIds: string[];
  startDate?: string;
  endDate?: string;
  status: CallStatus[];
  phoneNumber?: string;
  officePhoneNumbers?: string[];
  name?: string;
  callDirection?: CallDirection;
};

type CallRecordsPageSetting = PaginationSettings & {
  filters: CallRecordsFiltersType | undefined;
};

type AnalyticsFilterType = {
  locationIds: string[];
  startDate?: string;
  endDate?: string;
};

type FilterArgs =
  | [AllCallsTable.RecentCalls, CallRecordsFiltersType]
  | [AllCallsTable.Voicemails, VoicemailFiltersType]
  | [AllCallsTable.Unread_Voicemails, VoicemailFiltersType]
  | [AllCallsTable.Analytics, AnalyticsFilterType];

interface PhonePageSettingStore {
  config: {
    [AllCallsTable.RecentCalls]: CallRecordsPageSetting;
    [AllCallsTable.Voicemails]: VoicemailPageSetting;
    [AllCallsTable.Unread_Voicemails]: VoicemailPageSetting;
    [AllCallsTable.Analytics]: { filters: AnalyticsFilterType | undefined };
  };
  setPageSize: (tableName: AllCallsTable, rowCount: number) => void;
  setPageNumber: (tableName: AllCallsTable, pageNumber: number) => void;
  setFilters: (...args: FilterArgs) => void;
}

const DEFAULT_RECENT_CALLS_PAGE_SIZE = 25;
const DEFAULT_VOICEMAILS_PAGE_SIZE = 25;
const DEFAULT_UNREAD_VOICEMAILS_WIDGET_SIZE = 3;

// usePhonePageSettingStore holds all the settings for the phone page including filters applied,
// page number and page size for each table
export const usePhonePageSettingStore = createStoreWithSubscribe<PhonePageSettingStore>(
  (set) => ({
    config: {
      [AllCallsTable.RecentCalls]: {
        pageSize: DEFAULT_RECENT_CALLS_PAGE_SIZE,
        pageNumber: 1,
        filters: undefined,
      },
      [AllCallsTable.Voicemails]: {
        pageSize: DEFAULT_VOICEMAILS_PAGE_SIZE,
        pageNumber: 1,
        filters: undefined,
      },
      [AllCallsTable.Unread_Voicemails]: {
        pageSize: DEFAULT_UNREAD_VOICEMAILS_WIDGET_SIZE,
        pageNumber: 1,
        filters: undefined,
      },
      [AllCallsTable.Analytics]: {
        filters: undefined,
      },
    },

    setPageSize: (tableName, pageSize) => {
      set((state) => {
        if ('pageSize' in state.config[tableName]) {
          state.config[tableName].pageSize = pageSize;
        }
      });
    },
    setPageNumber: (tableName, pageNumber) => {
      set((state) => {
        if ('pageNumber' in state.config[tableName]) {
          state.config[tableName].pageNumber = pageNumber;
        }
      });
    },
    setFilters: (tableName, filters) => {
      set((state) => {
        if ('filters' in state.config[tableName]) {
          state.config[tableName].filters = filters;
        }
      });
    },
  }),
  {
    name: 'PhoneConfigStateStore',
    trace: true,
  }
);

export const usePhonePageSettingShallowStore = createShallowStore<PhonePageSettingStore>(usePhonePageSettingStore);
